
import React, { useState, useMemo, useRef, useEffect } from 'react';
import type { Chef, Language } from '../types';
import { getTranslation } from '../utils/translations';
import {
    chefMatchesQuery,
    getChefDisplayName,
    getPickableChefs,
    canUserPickChef,
    isNicknameLockedForUser,
} from '../utils/chefDisplay';
import XIcon from './icons/XIcon';
import ChefAvatar from './ChefAvatar';

interface ChefAutocompleteProps {
    chefs: Chef[];
    value: string | null;
    onChange: (chefId: string | null) => void;
    currentUserId?: string;
    language?: Language;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
}

const ChefAutocomplete: React.FC<ChefAutocompleteProps> = ({
    chefs,
    value,
    onChange,
    currentUserId,
    language = 'en',
    placeholder,
    label,
    disabled = false,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [query, setQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const pickableChefs = useMemo(
        () => getPickableChefs(chefs, currentUserId),
        [chefs, currentUserId]
    );

    const selectedChef = useMemo(
        () => (value ? chefs.find((c) => c.id === value) ?? null : null),
        [chefs, value]
    );

    const isLockedSelection = useMemo(
        () => !!selectedChef && !canUserPickChef(selectedChef, currentUserId),
        [selectedChef, currentUserId]
    );

    const isInputDisabled = disabled || isLockedSelection;

    useEffect(() => {
        if (selectedChef && !query) {
            setQuery(getChefDisplayName(selectedChef, language));
        }
    }, [selectedChef, language, query]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isTypingLockedNickname = useMemo(() => {
        if (!query.trim() || isLockedSelection) return false;
        return isNicknameLockedForUser(chefs, query.trim(), currentUserId);
    }, [chefs, query, currentUserId, isLockedSelection]);

    const suggestions = useMemo(() => {
        if (!query.trim() || isInputDisabled) return [];
        const q = query.trim();
        return pickableChefs
            .filter((c) => chefMatchesQuery(c, q, language) && c.id !== value)
            .slice(0, 8);
    }, [pickableChefs, query, language, value, isInputDisabled]);

    const handleSelect = (chef: Chef) => {
        onChange(chef.id);
        setQuery(getChefDisplayName(chef, language));
        setShowSuggestions(false);
    };

    const handleClear = () => {
        if (isLockedSelection) return;
        onChange(null);
        setQuery('');
        setShowSuggestions(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isLockedSelection) return;
        const next = e.target.value;
        setQuery(next);
        setShowSuggestions(true);
        if (value && selectedChef && next !== getChefDisplayName(selectedChef, language)) {
            onChange(null);
        }
    };

    return (
        <div ref={containerRef} className="relative">
            {label && (
                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 px-1">
                    {label}
                </label>
            )}
            <div className="relative">
                {selectedChef?.imageUrl && (
                    <div className="absolute start-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                        <ChefAvatar
                            imageUrl={selectedChef.imageUrl}
                            name={getChefDisplayName(selectedChef, language)}
                            size="sm"
                            variant="thumb"
                        />
                    </div>
                )}
                <input
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => !isLockedSelection && setShowSuggestions(true)}
                    disabled={isInputDisabled}
                    placeholder={placeholder || t('selectChef')}
                    className={`w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl py-3 text-sm focus:ring-2 focus:ring-sky-500 outline-none disabled:opacity-50 ${selectedChef?.imageUrl ? 'ps-14 pe-4' : 'px-4'}`}
                />
                {value && !isInputDisabled && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute end-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    >
                        <XIcon className="w-4 h-4" />
                    </button>
                )}
            </div>
            {(isLockedSelection || isTypingLockedNickname) && (
                <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 px-1 font-bold">
                    {t('chefNicknameLocked')}
                </p>
            )}
            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-[400] w-full mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden max-h-48 overflow-y-auto">
                    {suggestions.map((chef) => (
                        <button
                            key={chef.id}
                            type="button"
                            onClick={() => handleSelect(chef)}
                            className="w-full text-start px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-100 dark:border-slate-800 last:border-0 transition-colors flex items-center gap-3"
                        >
                            <ChefAvatar
                                imageUrl={chef.imageUrl}
                                name={getChefDisplayName(chef, language)}
                                size="sm"
                                variant="thumb"
                            />
                            <span className="min-w-0">
                                <span className="font-bold text-slate-800 dark:text-slate-100 block">
                                    {getChefDisplayName(chef, language)}
                                </span>
                                {chef.name && chef.englishName && chef.name !== chef.englishName && (
                                    <span className="block text-[10px] text-slate-400 mt-0.5">{chef.englishName}</span>
                                )}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ChefAutocomplete;
